import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, Typography, Snackbar, Alert, Box, Grid, LinearProgress, useMediaQuery, useTheme } from "@mui/material";
import moment from "moment";
import IMasterClass from "../../../types/MasterClass";
import IEvent from "../../../types/Event";
import IPaciente from "../../../types/Paciente";
import ClaseUtils from "../../../utils/Clase";
import ConfigUtils from "../../../utils/Config";
import { formatCurrency } from "../../../utils/Factura";
import WavesIcon from '@mui/icons-material/Waves';
import LandscapeIcon from '@mui/icons-material/Landscape';
import DiamondIcon from '@mui/icons-material/Diamond';
import GrassIcon from '@mui/icons-material/Grass';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import InfoIcon from "@mui/icons-material/Info";
import ISala from "../../../types/Sala";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import { useNavigate } from "react-router-dom";
import IAlert from "../../../types/Alert";
import * as constantes from "../../../common/Constantes";
import FacturacionService from "../../../services/FacturacionService";
import ConfigurarPrecioClase from "./ConfigurarPrecioClase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SettingsIcon from "@mui/icons-material/Settings";
import { formatDateOnly, toDateOnly } from "../../../utils/Agenda";
import IServiceClassInvoice from "../../../types/ServiceClassInvoice";

interface FacturacionClaseProps {
    open: boolean;
    onClose: () => void;
    masterClass: IMasterClass | undefined;
    parejas: IMasterClass[]; // clases creadas junto con esta (p. ej. martes y jueves a la misma hora)
    selectedEvent: IEvent | undefined;
    pacientes: IPaciente[];
    facturacion: boolean;
    onModificarPacientes: (masterIds: string[]) => void;
    onEditarClase: (masterIds: string[]) => void;
}

// Días de la semana en plural y en minúscula, unidos con comas e «y»: «martes y jueves», «lunes, miércoles y viernes»
const unirDias = (weekDays: number[]): string => {
  const nombres = [...weekDays].sort((a, b) => a - b).map(d => {
    const nombre = constantes.weekNames[d].toLowerCase();
    return nombre.endsWith('s') ? nombre : `${nombre}s`;
  });
  return nombres.length > 1 ? `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}` : nombres[0] ?? '';
};
const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);
const fechaCorta = (fecha: Date) => new Date(fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

const FacturacionClase = ({ open, onClose, masterClass, parejas, selectedEvent, pacientes, facturacion, onModificarPacientes, onEditarClase }: FacturacionClaseProps) => {
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  // Móvil: la ficha ocupa toda la pantalla
  const pantallaMovil = useMediaQuery(useTheme().breakpoints.down("sm"));
  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });
  const [loading, setLoading] = useState(false);
  const [configPacientes, setConfigPacientes] = useState<Record<string, IServiceClassInvoice | null>>({});
  const [pacienteEditar, setPacienteEditar] = useState<IPaciente | null>(null);
  const [modalPrecioOpen, setModalPrecioOpen] = useState(false);
  const userRole: number = ConfigUtils.getUserRole();
  
  const handleOpenModalPrecio = (paciente: IPaciente) => {
      setPacienteEditar(paciente);
      setModalPrecioOpen(true);
  };
  
  const handleCloseModalPrecio = () => {
      setModalPrecioOpen(false);
      setPacienteEditar(null);
  };
  
  const pacientesActivosEnClase = React.useMemo(() => {
    if (!masterClass || !selectedEvent) return [];

    const fechaClase = toDateOnly(selectedEvent.start);

    return masterClass.pacientes.filter(p => {
      // Los tramos se guardan como día UTC: leídos en hora local, el final del día 16 (23:59 UTC) caía en el 17 en España
      const start = ClaseUtils.diaDeTramo(p.start);
      const end = ClaseUtils.diaDeTramo(p.end);
      return start.getTime() <= fechaClase.getTime() && end.getTime() >= fechaClase.getTime();
    }).filter((p, i, lista) => lista.findIndex(x => x.id === p.id) === i);
  }, [masterClass, selectedEvent]);
  
  useEffect(() => {
      if (!open || !masterClass || pacientesActivosEnClase.length === 0) return;
      if (!facturacion) return;
      load();
  }, [open, masterClass, pacientesActivosEnClase]);

  const load = async () => {
    try {
        setLoading(true);
        const pacienteIds = pacientesActivosEnClase.map(p => p.id);
        const fechaClase = formatDateOnly(selectedEvent!.start);
        const response = await FacturacionService.getFacturacionClase(masterClass?._id!, pacienteIds, fechaClase!);
        const configMap: Record<string, IServiceClassInvoice | null> = {};
        response.data.forEach((p: any) => {
          configMap[p?.pacienteId ?? ""] = p
          ? {
              _id: p._id,
              userId: p.userId,
              pacienteId: p.pacienteId,
              actividadId: p.actividadId,
              nombre: p.nombre || "",
              concepto: p.concepto || "",
              precio: p.precio != null ? parseFloat(p.precio) : 0,
              impuesto: p.impuesto ?? 0,
              facturable: p.facturable ?? true,
              frecuencia: p.frecuencia || "mensual",
              activo: true,
              motivoNoFacturable: p.motivoNoFacturable,
              clasesSemanales: p.clasesSemanales
            }
          : null;
        })
        setConfigPacientes(configMap);
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "FacturacionClase.Component.load",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const salas: ISala[] = ConfigUtils.getSalas();
  const actividades = ConfigUtils.getActividades();
  const actividad = ClaseUtils.actividadDe(actividades, masterClass?.title);
  const capacidad = actividad?.capacity ?? 0;
  const especialista = ConfigUtils.getEmpleados().find(e => e.internalID === masterClass?.especialistaId)?.firstName;

  // Modificar pacientes o editar la clase: si tiene pareja, antes se pregunta a qué clases aplicar el cambio
  const [aplicarA, setAplicarA] = useState<'pacientes' | 'clase' | null>(null);
  const abrirEdicion = (tipo: 'pacientes' | 'clase', masterIds: string[]) =>
    tipo === 'pacientes' ? onModificarPacientes(masterIds) : onEditarClase(masterIds);
  const elegirClases = (tipo: 'pacientes' | 'clase') => {
    if (!masterClass?._id) return;
    if (parejas.length === 0) abrirEdicion(tipo, [masterClass._id]);
    else setAplicarA(tipo);
  };
  const aplicar = (masterIds: string[]) => {
    if (aplicarA) abrirEdicion(aplicarA, masterIds);
    setAplicarA(null);
  };

  const pacientesOrdenados = React.useMemo(() => {
    return pacientesActivosEnClase
      .map(p => {
        const paciente = pacientes.find(x => x._id === p.id);
        if (!paciente) return null;

        const config = configPacientes[paciente._id];

        let orden = 0;

        if (!config) {
          orden = 0; // Sin configuración
        } else if (config.facturable) {
          orden = 1; // Facturable
        } else {
          orden = 2; // No facturable
        }

        return {
          paciente,
          config,
          orden
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.orden - b.orden);
  }, [pacientesActivosEnClase, pacientes, configPacientes, facturacion]);

  return (
      <>
      <Dialog open={open} maxWidth="sm" fullWidth fullScreen={pantallaMovil} onClose={onClose}>
          <DialogTitle sx={{ pb: 0 }}>{actividad?.title ?? ''}</DialogTitle>
          <DialogContent>
              <Typography>{ClaseUtils.formatClaseDate(selectedEvent)}</Typography>
              <Typography>{ClaseUtils.formatClaseTime(selectedEvent)}</Typography>
              {parejas.length > 0 && (
                <Typography variant="body2" color="text.secondary">{`Esta clase también se da los ${unirDias(parejas.map(m => m.weekDay))}`}</Typography>
              )}

              {/* Datos de la serie */}
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Especialista</Typography>
                  <Typography>{especialista ?? '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Sala</Typography>
                  <div>
                    {(() => {
                      const sala = salas.find(s => String(s.internalID) === String(masterClass?.salaId));
                      if (!sala) {
                        return "Sala no encontrada";
                      }

                    let icon;
                    switch (sala.icon) {
                      case "0":
                        icon = <WavesIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                        break;
                      case "1":
                        icon = <DiamondIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                        break;
                      case "2":
                        icon = <LandscapeIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                        break;
                      case "3":
                        icon = <GrassIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                        break;
                      case "4":
                        icon = <AcUnitIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                        break;
                      default:
                        break;
                    }
                
                    return (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {icon}
                        <span>{sala.title}</span>
                      </div>
                    );
                  })()}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Fecha inicio - fin</Typography>
                  <Typography>{masterClass ? `${fechaCorta(masterClass.start)} - ${fechaCorta(masterClass.terminarRepetir)}` : ''}</Typography>
                </Grid>
              </Grid>

              {/* Ocupación: pacientes de la serie ese día */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Pacientes inscritos</Typography>
                  <Typography>{`${pacientesActivosEnClase.length}/${capacidad}`}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  color={pacientesActivosEnClase.length > capacidad ? 'warning' : 'primary'}
                  value={capacidad > 0 ? Math.min(100, pacientesActivosEnClase.length / capacidad * 100) : 0}
                  sx={{ height: 6, borderRadius: 3, my: 0.5 }}
                />
                {masterClass && (
                  <Typography variant="caption" color="text.secondary">
                    {`Pacientes inscritos para ${actividad?.title ?? ''} todos los ${unirDias([masterClass.weekDay])} a las ${moment(masterClass.start).format('H:mm')}.`}
                  </Typography>
                )}
              </Box>
              {pacientesActivosEnClase.length === 0 && <Typography sx={{ mt: 1 }} color="text.secondary">No hay pacientes inscritos en esta clase</Typography>}

              {facturacion ?
              <div>
                {loading ? (
                  <Typography>Cargando información de facturación...</Typography>
                ) : (
                  <List dense>
                    {pacientesOrdenados.map((p, i) => {
                      const paciente = pacientes.find(x => x._id === p?.paciente._id);
                      if (!paciente) return null;
                    
                      const config = configPacientes[paciente._id];
                    
                      return (
                        <ListItem key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            {`${paciente.nombre} ${paciente.apellidos}`}

                            {config?._id ? (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ marginLeft: 1, color: config.facturable ? "green" : "gray", display: "flex", alignItems: "center", gap: 0.5 }}
                                >
                                  {!config.facturable && config.motivoNoFacturable && (
                                    <Tooltip title={config.motivoNoFacturable}>
                                      <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                  )}
                                  {config.facturable
                                    ? `${formatCurrency(config.precio, ConfigUtils.getUserCurrency())} / mes`
                                    : "Paciente no facturable"}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ marginLeft: 1, color: "gray" }}>
                                Sin configuración
                              </Typography>
                            )}
                          </div>
                          
                          {userRole !== constantes.ROLES.SANITARIO && <div style={{ display: "flex", gap: 4 }}>
                            <Tooltip title={config?._id ? "Editar configuración" : "Añadir configuración"}>
                              <IconButton
                                color={
                                  !config?._id
                                    ? "default"
                                    : !config.facturable
                                      ? "warning"
                                      : "success"
                                }
                                onClick={() => handleOpenModalPrecio(paciente)}
                              >
                                <SettingsIcon />
                              </IconButton>
                            </Tooltip>
                          </div>}
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </div> : 
              <ul>
                {pacientesOrdenados.map((p, i) => {
                  const paciente = pacientes.find(x => x._id === p?.paciente._id);
                  if (!paciente) return null;
                                
                  return (
                      <div key={p?.paciente._id}><li>
                        {`${paciente.nombre} ${paciente.apellidos}`}
                      </li></div>
                  );
                })}
              </ul>}
              
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              {masterClass && <Button onClick={() => elegirClases('pacientes')}>Modificar pacientes</Button>}
              {masterClass && <Button onClick={() => elegirClases('clase')}>Editar clase</Button>}
              <Button onClick={onClose}>Cerrar</Button>
          </DialogActions>
      </Dialog>

      {/* CLASE CON PAREJA: el usuario decide si el cambio es para esta clase o para todas */}
      <Dialog open={aplicarA !== null} maxWidth="xs" fullWidth onClose={() => setAplicarA(null)}>
          <DialogTitle>¿A qué clases aplicas el cambio?</DialogTitle>
          {masterClass && (
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => aplicar([masterClass._id!])}>
                {`Solo ${unirDias([masterClass.weekDay])} a las ${moment(masterClass.start).format('H:mm')}`}
              </Button>
              <Button variant="outlined" sx={{ ml: '0 !important' }} onClick={() => aplicar([masterClass._id!, ...parejas.map(m => m._id!)])}>
                {`${capitalizar(unirDias([masterClass.weekDay, ...parejas.map(m => m.weekDay)]))} a las ${moment(masterClass.start).format('H:mm')}`}
              </Button>
              <Button sx={{ ml: '0 !important' }} onClick={() => setAplicarA(null)}>Cancelar</Button>
            </DialogActions>
          )}
      </Dialog>

      {modalPrecioOpen && pacienteEditar && (
        <ConfigurarPrecioClase
          open={modalPrecioOpen}
          onClose={handleCloseModalPrecio}
          paciente={pacienteEditar}
          masterClass={masterClass!}
          config={configPacientes[pacienteEditar._id]}
          onSave={(nuevaConfig) => {
            setConfigPacientes(prev => ({
              ...prev,
              [pacienteEditar._id]: {
                ...prev[pacienteEditar._id] || {},
                ...nuevaConfig
              }
            }));
            handleCloseModalPrecio();
          }}
        />
      )}

      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.msg}
        </Alert>
      </Snackbar>
      </>
  );
};

export default FacturacionClase;
